import { useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import AuthLayout from '../components/AuthLayout';
import PasswordInput from '../components/PasswordInput';
import { ROUTE_LOGIN } from '../constants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { resetPassword } from '../utils/apiUtils';
import { isValidPassword } from '../utils/validationUtils';
import { getUserReadableError } from '../utils/utils';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({password: '', confirmPassword: ''});
  const [isLoading, setIsLoading] = useState(false);

  const passwordsMatch = credentials.password === credentials.confirmPassword && credentials.confirmPassword.length > 0;
  const canSubmit = isValidPassword(credentials.password) && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const code = new URLSearchParams(location.search).get('code');

    if (!code) {
      showErrorMessage('Invalid or missing reset code');
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword({ code, password: credentials.password, confirmPassword: credentials.confirmPassword });
      showSuccessMessage('Password reset successfully');
      navigate(ROUTE_LOGIN);
    }
    catch (error) {
      console.error('unable to reset Password', error);
      showErrorMessage(getUserReadableError(error) ?? 'Unable to reset password. Please try again later.');
      if(error?.response?.status === 404) {
        navigate(ROUTE_LOGIN);
      }
    } finally {
      setIsLoading(false);
    }
  }
  
  return (
    <AuthLayout
      icon={<img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />}
      title="Reset your password"
    >
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="input-group">
          <label htmlFor="password" className="input-label">New password</label>
          <PasswordInput
            id="password"
            placeholder="Create a strong password"
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            autoComplete="new-password"
            showStrength={true}
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="confirmPassword" className="input-label">Confirm new password</label>
          <PasswordInput
            id="confirmPassword"
            placeholder="Re-enter your new password"
            value={credentials.confirmPassword}
            onChange={(e) => setCredentials({ ...credentials, confirmPassword: e.target.value })}
            autoComplete="new-password"
            required
          />
          {credentials.confirmPassword.length > 0 && !passwordsMatch && (
            <p className="auth-validation-error">Passwords don't match</p>
          )}
          {passwordsMatch && (
            <p className="auth-validation-success">Passwords match</p>
          )}
        </div>

        <button
          type="submit"
          className="auth-button"
          disabled={!canSubmit || isLoading}
        >
          {isLoading ? 'Resetting...' : 'Reset password'}
        </button>
      </form>
    </AuthLayout>
  );
}
export default ResetPassword;